// Contents of this file was produced with a help of a code generation tool and
// subsequently reviewed and edited by a human.
//
// While some of the code was created by an AI assisted service, manual adjustments
// have been made to ensure correctness, readability, functionality and compliance
// to Avalara's coding standards and guidelines.
// Any future modifications should be approached with caution to avoid
// overwriting the manual changes.
//
//
// This source code is owned by Avalara Inc. Unauthorized copying of this file,
// via any medium, is strictly prohibited. Proprietary and confidential.
//
// For more information, refer to the documentation or contact the development team.

import { PartnerLeadData, ProductPricingResponse, ApiError } from '../types';

/**
 * API client for partner-related endpoints
 */
class PartnersApi {
  private baseUrl = '/api/partners';

  /**
   * Generic fetch wrapper with error handling
   */
  private async fetchWithErrorHandling<T>(url: string): Promise<T> {
    try {
      const response = await fetch(url);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        throw new ApiError(
          errorData.error || `HTTP ${response.status}: ${response.statusText}`,
          response.status
        );
      }
      
      return response.json();
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(
        error instanceof Error ? error.message : 'Unknown error occurred'
      );
    }
  }

  /**
   * Fetch partner lead data by leadId
   */
  async getPartnerLeadData(leadId: string): Promise<PartnerLeadData> {
    const url = `${this.baseUrl}/lead/${encodeURIComponent(leadId)}`;
    return this.fetchWithErrorHandling<PartnerLeadData>(url);
  }

  /**
   * Fetch pricing for a single product.
   * Returns `null` when no active pricing exists for the product.
   */
  async getProductPricing(productId: number = 5): Promise<ProductPricingResponse> {
    const url = `${this.baseUrl}/products/pricing?id=${encodeURIComponent(productId)}`;
    return this.fetchWithErrorHandling<ProductPricingResponse>(url);
  }

  async getPartnerByName(partnerName: string): Promise<{ id: number }> {
    const url = `${this.baseUrl}/name/${encodeURIComponent(partnerName)}`;
    return this.fetchWithErrorHandling<{ id: number }>(url);
  }

  /**
   * Fetch partner configuration by partner ID
   */
  async getPartnerConfig(partnerId: string): Promise<any> {
    const url = `${this.baseUrl}/${encodeURIComponent(partnerId)}`;
    return this.fetchWithErrorHandling<any>(url);
  }

  /**
   * Validate sourceId (Airbnb only)
   */
  async validateSourceId(sourceId: string): Promise<{
    isValid: boolean;
    partnerId?: number;
    partnerSourceId?: string;
  }> {
    const url = `${this.baseUrl}/source/${encodeURIComponent(sourceId)}/validate`;
    return this.fetchWithErrorHandling<{
      isValid: boolean;
      partnerId?: number;
      partnerSourceId?: string;
    }>(url);
  }
}

// Export singleton instance
export const partnersApi = new PartnersApi();
